import {isFeatureEnabled} from '@github-ui/feature-flags'
import {searchUrl} from '@github-ui/issue-url-helper'
import type {AssigneePickerAssignee} from '@github-ui/item-picker-assignee-relay'
import {AssigneeRepositoryPicker} from '@github-ui/item-picker-assignee-relay'
import {formatActorLogin} from '@github-ui/item-picker-assignee-relay/utils/assignee-picker'
import {PersonIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, Button} from '@primer/react'
import type React from 'react'
import {useCallback, useMemo} from 'react'

import {LABELS} from '../../../constants/labels'
import {useQueryContext, useQueryDebouncedSearch, useQuerySearchState} from '../../../contexts/QueryContext'
import {getTokensByType, replaceAllFiltersByTypeInSearchQuery} from '../../../utils/urls'
import styles from './ListAuthorFilter.module.css'
import type {FilterBarPickerProps} from './ListItemsHeaderWithoutBulkActions'

export function ListAuthorFilter({repo: scopedRepo, applySectionFilter, nested}: FilterBarPickerProps) {
  const {name: repo, owner} = scopedRepo
  const {currentViewId} = useQueryContext()
  const {activeSearchQuery} = useQuerySearchState()
  const {debouncedDirtySearchQuery} = useQueryDebouncedSearch()
  const includeBots = isFeatureEnabled('issues_react_include_bots_in_pickers')

  const query = debouncedDirtySearchQuery ?? activeSearchQuery

  const currentAuthorTokens = useMemo(() => getTokensByType(query, 'author'), [query])

  const onSelectionChanged = useCallback(
    (selectedAuthors: AssigneePickerAssignee[]) => {
      const authors = selectedAuthors.map(formatActorLogin)
      const newQuery = replaceAllFiltersByTypeInSearchQuery(query, authors, 'author')
      const url = searchUrl({viewId: currentViewId, query: newQuery})
      applySectionFilter(newQuery, url)
    },
    [query, applySectionFilter, currentViewId],
  )

  const props = {
    readonly: false,
    title: LABELS.filters.authorLabel,
    assigneeTokens: currentAuthorTokens,
    assignees: [],
    repo,
    owner,
    onSelectionChange: onSelectionChanged,
    anchorElement: nested ? NestedAuthorsAnchor : AuthorsAnchor,
    showNoMatchItem: true,
  }

  return (
    <AssigneeRepositoryPicker
      name="author"
      selectionVariant="single"
      {...props}
      includeAuthorableBots={includeBots}
      includeAssignableBots={false}
    />
  )
}

function NestedAuthorsAnchor(props: React.HTMLAttributes<HTMLElement>) {
  return (
    <ActionList.Item {...props} aria-label={LABELS.filters.authorLabel} role="menuitem">
      <ActionList.LeadingVisual>
        <PersonIcon />
      </ActionList.LeadingVisual>
      {LABELS.filters.author}...
    </ActionList.Item>
  )
}

function AuthorsAnchor(props: React.HTMLAttributes<HTMLButtonElement>) {
  return (
    <Button
      variant="invisible"
      data-testid="authors-anchor-button"
      trailingVisual={TriangleDownIcon}
      aria-label={LABELS.filters.authorLabel}
      className={styles.authorFilterButton}
      {...props}
    >
      {LABELS.filters.author}
    </Button>
  )
}

try{ ListAuthorFilter.displayName ||= 'ListAuthorFilter' } catch {}
try{ NestedAuthorsAnchor.displayName ||= 'NestedAuthorsAnchor' } catch {}
try{ AuthorsAnchor.displayName ||= 'AuthorsAnchor' } catch {}